import { createContext, useContext, useReducer, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import {
	GET_UNIT,
	CREATE_UNIT,
	UPDATE_UNIT,
	GET_UNITS_PAGINATE,
	DELETE_UNIT,
	GET_UNITS
} from 'graphql/units';

import { ACTION_TYPES, reducers } from './reducers';

const UnitsStateContext = createContext();
const UnitsDispatchContext = createContext();

const initialState = {
	rows: [],
	page: 1,
	perPage: 15
};

const UnitsProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducers, initialState);
	const [toDelete, setToDelete] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	return (
		<UnitsStateContext.Provider
			value={{
				...state,
				toDelete,
				setToDelete,
				deleteModalOpen,
				setDeleteModalOpen
			}}
		>
			<UnitsDispatchContext.Provider value={dispatch}>
				{children}
			</UnitsDispatchContext.Provider>
		</UnitsStateContext.Provider>
	);
};

const useUnits = () => {
	const context = useContext(UnitsStateContext);

	if (!context)
		throw new Error('useUnits must be used within an UnitsProvider');

	return context;
};

const useDispatch = () => {
	const dispatch = useContext(UnitsDispatchContext);

	if (dispatch === undefined)
		throw new Error('useDispatch must be used within a UnitsProvider');

	return dispatch;
};

const usePaginateUnits = () => {
	const dispatch = useDispatch();

	return useLazyQuery(GET_UNITS_PAGINATE, {
		onCompleted: (data) => {
			window.history.replaceState(
				null,
				'',
				`./unidades?page=${data.paginateUnits.paginatorInfo.currentPage}&size=${data.paginateUnits.paginatorInfo.perPage}`
			);
			dispatch({ type: ACTION_TYPES.LIST, data: data.paginateUnits });
		}
	});
};

const useGetUnits = () => useQuery(GET_UNITS);

const useCreateUnit = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess, onError } = options;

	return useMutation(CREATE_UNIT, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.ADD, data: data.createUnit });
		},
		onError: (error) => {
			if (onError) onError(error);
		}
	});
};

const useUnit = (id) => {
	const dispatch = useContext(UnitsDispatchContext);

	return useQuery(
		GET_UNIT,
		{
			fetchPolicy: 'no-cache',
			variables: {
				id
			}
		},
		{
			onCompleted: (data) => {
				dispatch({ type: ACTION_TYPES.VIEW, data: data.unit });
			}
		}
	);
};

const useUpdateUnit = (options = {}) => {
	const dispatch = useContext(UnitsDispatchContext);
	const { onSuccess, onError } = options;

	return useMutation(UPDATE_UNIT, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.UPDATE, data: data.updateUnit });
		},
		onError: (error) => {
			if (onError) onError(error);
		},
		refetchQueries: [
			{
				query: GET_UNITS_PAGINATE
			}
		]
	});
};

const useDeleteUnit = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess } = options;
	return useMutation(DELETE_UNIT, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess();
			dispatch({ type: ACTION_TYPES.REMOVE, data: data.deleteUnit });
		}
	});
};

export {
	UnitsProvider,
	useUnits,
	useGetUnits,
	useCreateUnit,
	usePaginateUnits,
	useUnit,
	useUpdateUnit,
	useDeleteUnit
};
